import type { BotLevel, Variant } from '@/shared/game/types'

/**
 * Как человек обычно играет: сколько за столом, насколько силён компьютер,
 * какие правила.
 *
 * Раньше это были шаги перед партией — «сколько игроков», потом «какая
 * сложность», — и каждая партия начиналась с тех же трёх нажатий, что и
 * прошлая. Теперь выбор запоминается и меняется на главной, а кнопка режима
 * сразу запускает партию: так устроены почти все мобильные настолки.
 */
export type Prefs = {
  seats: 2 | 3 | 4
  level: BotLevel
  variant: Variant
}

const KEY = 'ludo.prefs'

const DEFAULTS: Prefs = { seats: 2, level: 'normal', variant: 'classic' }

export function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem(KEY)

    if (raw === null) return DEFAULTS

    const saved = JSON.parse(raw) as Partial<Prefs>

    // Поштучно: запись могла остаться от старой версии или быть испорчена,
    // и незнакомое значение не должно ломать главную.
    return {
      seats: saved.seats === 3 || saved.seats === 4 ? saved.seats : DEFAULTS.seats,
      level:
        saved.level === 'easy' || saved.level === 'hard' || saved.level === 'normal'
          ? saved.level
          : DEFAULTS.level,
      variant: saved.variant === 'bonus' ? 'bonus' : DEFAULTS.variant,
    }
  } catch {
    return DEFAULTS
  }
}

export function savePrefs(prefs: Prefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs))
  } catch {
    // Приватный режим: выбор проживёт до перезагрузки, и только.
  }
}

/** «вдвоём», «втроём», «вчетвером» — для подписей под кнопками. */
export const SEATS_WORD: Record<Prefs['seats'], string> = {
  2: 'вдвоём',
  3: 'втроём',
  4: 'вчетвером',
}

export const LEVEL_WORD: Record<BotLevel, string> = {
  easy: 'лёгкий',
  normal: 'средний',
  hard: 'сложный',
}
